import { BaseService } from './BaseService';
import type {
  AgentSessionDto,
  AgentMessageDto,
  CreateSessionRequest,
  UpdateSessionRequest,
  SendMessageRequest,
  SessionFilters,
} from '../types/session';
import type { PagedResult } from '../types/tenant';
import type { ContextUsage, AgentContextSummaryDto } from '../types/contextWindow';

/**
 * Session service for handling agent session operations.
 */
export class SessionService extends BaseService {
  /**
   * Gets a paginated list of agent sessions with optional filtering.
   */
  async getSessions(filters?: SessionFilters): Promise<PagedResult<AgentSessionDto>> {
    const queryParams = new URLSearchParams();
    if (filters?.pageNumber) queryParams.append('pageNumber', filters.pageNumber.toString());
    if (filters?.pageSize) queryParams.append('pageSize', filters.pageSize.toString());
    if (filters?.agentDefinitionId) queryParams.append('agentDefinitionId', filters.agentDefinitionId);
    if (filters?.status !== undefined) queryParams.append('status', filters.status.toString());
    if (filters?.searchTerm) queryParams.append('searchTerm', filters.searchTerm);
    if (filters?.startDate) queryParams.append('startDate', filters.startDate);
    if (filters?.endDate) queryParams.append('endDate', filters.endDate);

    const url = `/sessions${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return this.get<PagedResult<AgentSessionDto>>(url);
  }

  /**
   * Gets a session by ID.
   */
  async getSession(id: string): Promise<AgentSessionDto> {
    return this.get<AgentSessionDto>(`/sessions/${id}`);
  }

  /**
   * Creates a new session.
   */
  async createSession(data: CreateSessionRequest): Promise<AgentSessionDto> {
    return this.post<AgentSessionDto>('/sessions', data);
  }

  /**
   * Updates an existing session.
   */
  async updateSession(id: string, data: UpdateSessionRequest): Promise<AgentSessionDto> {
    return this.put<AgentSessionDto>(`/sessions/${id}`, data);
  }

  /**
   * Deletes a session.
   */
  async deleteSession(id: string): Promise<void> {
    return this.delete<void>(`/sessions/${id}`);
  }

  /**
   * Pauses a session.
   */
  async pauseSession(id: string): Promise<AgentSessionDto> {
    return this.post<AgentSessionDto>(`/sessions/${id}/pause`);
  }

  /**
   * Resumes a session.
   */
  async resumeSession(id: string): Promise<AgentSessionDto> {
    return this.post<AgentSessionDto>(`/sessions/${id}/resume`);
  }

  /**
   * Gets messages for a session.
   */
  async getMessages(
    sessionId: string,
    pageNumber?: number,
    pageSize?: number
  ): Promise<AgentMessageDto[]> {
    const queryParams = new URLSearchParams();
    if (pageNumber) queryParams.append('pageNumber', pageNumber.toString());
    if (pageSize) queryParams.append('pageSize', pageSize.toString());

    const url = `/sessions/${sessionId}/messages${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return this.get<AgentMessageDto[]>(url);
  }

  /**
   * Sends a message to a session.
   */
  async sendMessage(sessionId: string, data: SendMessageRequest): Promise<AgentMessageDto> {
    return this.post<AgentMessageDto>(`/sessions/${sessionId}/messages`, data);
  }

  /**
   * Gets the current context window usage for a session.
   */
  async getContextUsage(sessionId: string): Promise<ContextUsage> {
    return this.get<ContextUsage>(`/sessions/${sessionId}/context/usage`);
  }

  /**
   * Gets stored context summaries for a session.
   */
  async getContextSummaries(sessionId: string): Promise<AgentContextSummaryDto[]> {
    return this.get<AgentContextSummaryDto[]>(`/sessions/${sessionId}/context/summaries`);
  }
}

export const sessionService = new SessionService();
